import { useEffect, useState } from "react";
import { useSocialLinks, useCustomSocialLinks } from "@/hooks/content";
import { PageHeader, useToast } from "./components/admin-ui";

const fields: { key: keyof typeof defaults; label: string; icon: string }[] = [
  { key: "facebook", label: "Facebook", icon: "ri-facebook-fill" },
  { key: "youtube", label: "YouTube", icon: "ri-youtube-fill" },
  { key: "tiktok", label: "TikTok", icon: "ri-tiktok-fill" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-fill" },
  { key: "twitter", label: "X / Twitter", icon: "ri-twitter-x-fill" },
  { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-fill" },
];

const defaults = {
  facebook: "",
  youtube: "",
  tiktok: "",
  instagram: "",
  twitter: "",
  whatsapp: "",
};

// Remix Icon options an admin can pick for a custom social platform.
const iconOptions = [
  { value: "ri-facebook-fill", label: "Facebook" },
  { value: "ri-youtube-fill", label: "YouTube" },
  { value: "ri-tiktok-fill", label: "TikTok" },
  { value: "ri-instagram-fill", label: "Instagram" },
  { value: "ri-twitter-x-fill", label: "X / Twitter" },
  { value: "ri-whatsapp-fill", label: "WhatsApp" },
  { value: "ri-linkedin-fill", label: "LinkedIn" },
  { value: "ri-telegram-fill", label: "Telegram" },
  { value: "ri-snapchat-fill", label: "Snapchat" },
  { value: "ri-pinterest-fill", label: "Pinterest" },
  { value: "ri-messenger-fill", label: "Messenger" },
  { value: "ri-discord-fill", label: "Discord" },
  { value: "ri-global-fill", label: "Website" },
  { value: "ri-mail-fill", label: "Email" },
  { value: "ri-music-2-fill", label: "TuneIn / Radio" },
  { value: "ri-spotify-fill", label: "Spotify" },
];

export default function SocialMediaPage() {
  const { record, loading, error, update } = useSocialLinks();
  const {
    items: customLinks,
    add: addCustom,
    remove: removeCustom,
  } = useCustomSocialLinks();
  const { show } = useToast();

  const [form, setForm] = useState({ ...defaults, ...record });
  const [dirty, setDirty] = useState(false);

  // New custom platform draft
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("ri-global-fill");
  const [newUrl, setNewUrl] = useState("");

  useEffect(() => {
    setForm({ ...defaults, ...record });
    setDirty(false);
  }, [record]);

  async function save() {
    await update(form);
    setDirty(false);
    show("Social links saved. They now appear across the website.");
  }

  function addPlatform() {
    const name = newName.trim();
    const url = newUrl.trim();
    if (!name || !url) {
      show("Please enter both a name and a link for the new platform.");
      return;
    }
    addCustom({
      id: `custom-${Date.now()}`,
      name,
      icon: newIcon,
      url,
    });
    setNewName("");
    setNewUrl("");
    setNewIcon("ri-global-fill");
    show(`"${name}" added. It now appears on the homepage and footer.`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Social Media"
        subtitle="Manage the social links shown across the site, and add new platforms."
        actions={
          <button
            type="button"
            onClick={save}
            disabled={!dirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </span>
            Save Changes
          </button>
        }
      />

      {loading ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center text-sm text-foreground-500">
          Loading…
        </div>
      ) : error ? (
        <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-sm text-accent-700">{error}</div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4">
              <div className="flex items-center gap-3 sm:w-44 shrink-0">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${f.icon} text-base`} />
                </span>
                <span className="text-sm font-medium text-foreground-800">{f.label}</span>
              </div>
              <input
                type="text"
                value={form[f.key]}
                onChange={(e) => {
                  setForm((p) => ({ ...p, [f.key]: e.target.value }));
                  setDirty(true);
                }}
                placeholder={`https://… ${f.label} URL`}
                className="flex-1 px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          ))}
        </div>
      )}

      {/* Custom platforms */}
      <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6">
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Add a new platform</h3>
        <p className="text-sm text-foreground-600 mb-4">
          Add any social media app or website link. It will appear on the homepage and in the footer immediately.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Platform name (e.g. LinkedIn)"
            className="px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
          <select
            value={newIcon}
            onChange={(e) => setNewIcon(e.target.value)}
            className="px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            {iconOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://… link"
            className="px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 lg:col-span-2"
          />
        </div>

        <button
          type="button"
          onClick={addPlatform}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-add-line text-base" />
          Add Platform
        </button>

        {customLinks.length > 0 && (
          <div className="mt-5 border-t border-background-100 pt-4">
            <p className="text-sm font-medium text-foreground-700 mb-3">Your custom platforms</p>
            <div className="space-y-2">
              {customLinks.map((link) => (
                <div key={link.id} className="flex items-center gap-3 px-3 py-2 bg-background-50 rounded-md">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-background-200 text-foreground-600">
                    <i className={`${link.icon} text-base`} />
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{link.name}</span>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:underline truncate flex-1"
                  >
                    {link.url}
                  </a>
                  <button
                    type="button"
                    onClick={() => removeCustom(link.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-600 hover:bg-accent-50 transition-colors"
                    aria-label={`Remove ${link.name}`}
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}